/**
 * Typed wrappers for the atomic unenroll RPCs added in migration
 * 20260727000003_fix_unenroll_data_loss.sql.
 *
 * WHY THESE EXIST: public.course_enrollments had RLS enabled with no DELETE
 * policy, so the client-side `DELETE FROM course_enrollments` at the end of
 * every unenroll flow was silently filtered out — PostgREST returned 200 with
 * zero rows affected and error === null, so `if (error) throw` never fired and
 * the UI reported success. Meanwhile the destructive progress deletes that ran
 * first had DELETE policies and succeeded. Learners lost all their work and
 * stayed enrolled.
 *
 * Each RPC performs the whole cascade in one transaction and RAISEs if the
 * enrollment delete affects zero rows. These helpers additionally assert that
 * the reported affected-row count is non-zero before the caller shows success.
 */

import { supabase } from "@/integrations/supabase/client";

export interface UnenrollResult {
  success: boolean;
  course_enrollments_deleted?: number;
  curriculum_enrollments_deleted?: number;
  user_id?: string;
  course_id?: string;
  curriculum_id?: string;
  cohort_id?: string;
}

/**
 * Invokes one of the unenroll RPCs and verifies rows actually changed.
 *
 * The cast is required only because these functions post-date the checked-in
 * generated Supabase types; it can be removed once types are regenerated.
 */
async function callUnenrollRpc(
  fn: "unenroll_from_course" | "unenroll_from_curriculum" | "remove_cohort_member",
  args: Record<string, string>,
  requiredCountKey: keyof UnenrollResult,
): Promise<UnenrollResult> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)(fn, args);

  if (error) throw error;

  const result = (data ?? {}) as UnenrollResult;

  if (!result.success || !result[requiredCountKey]) {
    throw new Error(
      "The unenroll did not remove any enrollment, so nothing was changed. Please try again.",
    );
  }

  return result;
}

export function unenrollFromCourse(courseId: string, userId?: string) {
  return callUnenrollRpc(
    "unenroll_from_course",
    userId ? { _course_id: courseId, _user_id: userId } : { _course_id: courseId },
    "course_enrollments_deleted",
  );
}

export function unenrollFromCurriculum(curriculumId: string, userId?: string) {
  return callUnenrollRpc(
    "unenroll_from_curriculum",
    userId ? { _curriculum_id: curriculumId, _user_id: userId } : { _curriculum_id: curriculumId },
    "curriculum_enrollments_deleted",
  );
}

export function removeCohortMember(cohortId: string, userId: string) {
  return callUnenrollRpc(
    "remove_cohort_member",
    { _cohort_id: cohortId, _user_id: userId },
    "course_enrollments_deleted",
  );
}
